import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIdToken } from 'react-firebase-hooks/auth';
import { useTranslation } from 'react-i18next';

import { auth as firebaseAuth } from '@/app/providers/firebase';
import {
  useGetPopularSearchesQuery,
  useGetTopUseCasesQuery,
} from '@/features/chat/services/cms';
import type { PopularSearch, TopUseCase } from '@/features/chat/types/cms';

interface CmsContextType {
  topUseCases: TopUseCase[];
  popularSearches: PopularSearch[];
  isTopUseCasesLoading: boolean;
  isPopularSearchesLoading: boolean;
}

const CmsContext = createContext<CmsContextType | null>(null);

interface CmsProviderProps {
  children: ReactNode;
}

export function CmsProvider({
  children,
}: Readonly<CmsProviderProps>): ReactNode {
  const { i18n } = useTranslation();
  const [, isAuthLoading] = useIdToken(firebaseAuth);
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  const [topUseCases, setTopUseCases] = useState<TopUseCase[]>([]);
  const [popularSearches, setPopularSearches] = useState<PopularSearch[]>([]);

  const { data: topUseCasesData, isLoading: isTopUseCasesLoading } =
    useGetTopUseCasesQuery({ locale }, { skip: isAuthLoading });

  const { data: popularSearchesData, isLoading: isPopularSearchesLoading } =
    useGetPopularSearchesQuery({ locale }, { skip: isAuthLoading });

  useEffect(() => {
    if (topUseCasesData !== undefined) {
      setTopUseCases(topUseCasesData);
    }
  }, [topUseCasesData]);

  useEffect(() => {
    if (popularSearchesData !== undefined) {
      setPopularSearches(popularSearchesData);
    }
  }, [popularSearchesData]);

  const value = useMemo<CmsContextType>(
    () => ({
      topUseCases,
      popularSearches,
      isTopUseCasesLoading,
      isPopularSearchesLoading,
    }),
    [
      topUseCases,
      popularSearches,
      isTopUseCasesLoading,
      isPopularSearchesLoading,
    ],
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms(): CmsContextType {
  const context = useContext(CmsContext);
  if (context === null) {
    throw new Error('useCms must be used within a CmsProvider');
  }
  return context;
}
